import { useRef, useState } from 'react';
import { uploadViaHandoff } from '../lib/api.ts';
import { Mark } from './Mark.tsx';

/**
 * The page a phone lands on after scanning the QR.
 *
 * Deliberately one control. Whoever holds the phone may not work here,
 * may never have seen the product, and is standing in front of
 * something broken. Anything beyond "take a photo" is friction.
 */
export const PhotoUpload = ({ token }: { readonly token: string }) => {
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>(
    'idle',
  );
  const [message, setMessage] = useState('');
  const [count, setCount] = useState(0);

  const send = async (file: File | undefined) => {
    if (!file) return;
    setState('busy');

    const result = await uploadViaHandoff(token, file);
    if (result.ok) {
      setCount((n) => n + 1);
      setState('done');
    } else {
      setState('error');
      setMessage(
        result.reason === 'not_found'
          ? 'This link has expired. Ask for a new code.'
          : result.reason === 'unsupported_type'
            ? 'That file type cannot be sent.'
            : 'Upload failed. Check your signal and try again.',
      );
    }

    if (input.current) input.current.value = '';
  };

  return (
    <main className="loginWrap">
      <div className="loginCard" style={{ textAlign: 'center' }}>
        <Mark size={46} />
        <h1>Send a photo</h1>

        {state === 'done' ? (
          <>
            <p className="allClear">
              Sent. {count > 1 ? `${count} photos so far.` : ''}
            </p>
            <p className="hint">
              You can add another, or just close this page.
            </p>
          </>
        ) : (
          <p className="hint">
            Take a photo of the problem. It goes straight onto the
            report &mdash; no sign-in needed.
          </p>
        )}

        {state === 'error' && <p className="err">{message}</p>}

        <input
          ref={input}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => void send(e.target.files?.[0])}
        />
        <button
          className="primary"
          disabled={state === 'busy'}
          onClick={() => input.current?.click()}
          style={{ fontSize: 17, padding: '15px 20px' }}
        >
          {state === 'busy'
            ? 'Sending...'
            : state === 'done'
              ? 'Take another'
              : 'Open camera'}
        </button>
      </div>
    </main>
  );
};
